import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import {
	performPostRequest,
	performGetRequest,
	performPutRequest,
} from '@atlassian/jira-fetch/src/utils/requests.tsx';
import type {
	AdminSettingsRequest,
	AdminSettingsResponse,
	AdminSettingsMap,
	BannerSettings,
} from '../common/types.tsx';
import {
	getAdminSettingsUrl,
	getBannerSettingsUrl,
	transformAdminSettingsResponse,
	transformBannerSettings,
} from '../common/utils.tsx';
import { emptyBannerSettingsResponse } from '../common/constants.tsx';

/**
 * Fetches admin settings by keys for a specific workspace
 * Based on POST /gateway/api/jsm/assets/workspace/<workspace-id>/v1/admin/settings
 */
export const getAdminSettingsByKeys = async (
	workspaceId: string,
	settingKeys: AdminSettingsRequest[],
): Promise<AdminSettingsMap> => {
	const url = getAdminSettingsUrl(workspaceId);

	try {
		const response = await performPostRequest<AdminSettingsResponse>(url, {
			body: JSON.stringify(settingKeys),
		});

		return transformAdminSettingsResponse(response);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		if (error.statusCode === 403) {
			return {};
		}

		fireErrorAnalytics({
			meta: {
				id: 'getAdminSettingsByKeys',
				packageName: 'jiraRouterResourcesAssetsAdminNavigationSettings',
				teamName: 'aurum',
			},
			error,
		});
		throw error;
	}
};

/**
 * Fetches banner settings for a specific workspace
 * Based on GET /gateway/api/jsm/assets/workspace/<workspace-id>/v1/admin/banner-settings
 */
export const getBannerSettings = async (workspaceId: string): Promise<BannerSettings> => {
	const url = getBannerSettingsUrl(workspaceId);

	try {
		const response = await performGetRequest<BannerSettings>(url);
		return transformBannerSettings(response);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		if (error.statusCode === 403 || error.statusCode === 404) {
			return emptyBannerSettingsResponse;
		}

		fireErrorAnalytics({
			meta: {
				id: 'getBannerSettings',
				packageName: 'jiraRouterResourcesAssetsAdminBannerSettings',
				teamName: 'aurum',
			},
			error,
		});
		throw error;
	}
};

/**
 * Updates banner settings for a specific workspace
 * Based on PUT /gateway/api/jsm/assets/workspace/<workspace-id>/v1/admin/banner-settings/<banner-id>
 */
export const updateBannerSettings = async (
	workspaceId: string,
	bannerId: string,
	bannerUpdates: BannerSettings,
): Promise<BannerSettings> => {
	const url = `${getBannerSettingsUrl(workspaceId)}/${bannerId}`;

	try {
		const response = await performPutRequest<BannerSettings>(url, {
			body: JSON.stringify(transformBannerSettings(bannerUpdates)),
		});
		return response;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		fireErrorAnalytics({
			meta: {
				id: 'updateBannerSettings',
				packageName: 'jiraRouterResourcesAssetsAdminBannerSettings',
				teamName: 'aurum',
			},
			error,
		});
		throw error;
	}
};
